import { getFriendlyLabel } from "@/components/ui/workflow-step-row/components/VariablePill";
import type { NodesExecutionState } from "@/types/runStream";
import type { WorkflowNodeConnector } from "@/stores/workflowsStore";
import type { Edge, Node } from "@xyflow/react";

// ============================================================================
// INTERFACES & TYPES
// ============================================================================

export interface WorkflowVariable {
  id: string;
  label: string;
  category: string;
  nodeId: string;
  nodeName: string;
  displayLabel?: string;
}

// ============================================================================
// HIERARCHICAL VARIABLE STRUCTURE (for new tree-based variable picker)
// ============================================================================

/**
 * A variable item that can have nested children (for tree view)
 */
export interface HierarchicalVariable {
  /** Full variable path: "{{node.result.field}}" */
  id: string;
  /** Just the field name: "field" */
  key: string;
  /** Human-readable label/description */
  label: string;
  /** Nested child variables (for objects with properties) */
  children?: HierarchicalVariable[];
  /** Friendly display label: "1. Manual Trigger: field" */
  displayLabel?: string;
}

/**
 * A category of variables within a node (e.g., Output, Payment, Request)
 */
export interface VariableCategory {
  /** Category key: "output", "payment", "request" */
  key: string;
  /** Display label: "Output", "Payment", "Request" */
  label: string;
  /** Sort order (lower = first) */
  order: number;
  /** Variables in this category */
  variables: HierarchicalVariable[];
}

/**
 * All variables from a single node, organized by category
 */
export interface NodeVariables {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  categories: VariableCategory[];
  stepNumber?: number;
}

/**
 * Schema extension for defining categories on result fields.
 * Add "x-category" to fields in resultSchema to control grouping.
 */
export interface CategorySchema {
  /** Category labels by key */
  "x-categoryLabels"?: Record<string, string>;
  /** Order of categories (first in array = first displayed) */
  "x-categoryOrder"?: string[];
}

export interface InvalidVariable {
  variable: string;
  nodeId: string;
  nodeName: string;
  fieldName: string;
}

// Type for payloads that may include x402 configuration
export interface PayloadWithX402 {
  x402?: {
    discoveryInput?: {
      schema?: {
        properties?: Record<string, { description?: string; type?: string }>;
      };
    };
  };
}

// Recursive schema property type to support nested objects
export interface SchemaProperty {
  description?: string;
  type?: string;
  properties?: Record<string, SchemaProperty>;
  additionalProperties?: boolean;
  items?: SchemaProperty;
}

export interface NodeDefinition {
  type: string;
  payloadSchema?: {
    properties?: Record<string, SchemaProperty>;
  };
  resultSchema?: {
    properties?: Record<string, SchemaProperty>;
  };
}

/**
 * Options for extracting nested variables from schema properties.
 */
export interface ExtractNestedVariablesOptions {
  basePath: string;
  properties: Record<string, SchemaProperty>;
  nodeId: string;
  nodeName: string;
  /** Static category (used when category is pre-determined) */
  category?: string;
  /** Node type for dynamic category lookup via VARIABLE_GROUPING_RULES */
  nodeType?: string;
  /** Root field name for dynamic category lookup */
  rootFieldName?: string;
  maxDepth?: number;
  currentDepth?: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** System variable for the current workflow ID. */
export const WORKFLOW_ID_VAR = "{{$workflowId}}";

/**
 * Default category configuration used when schema doesn't specify categories
 */
export const DEFAULT_CATEGORIES: Record<string, { label: string; order: number }> = {
  output: { label: "Output", order: 1 },
  payment: { label: "Payment", order: 2 },
  request: { label: "Request", order: 3 },
  config: { label: "Config", order: 4 },
  context: { label: "Context", order: 5 },
  connector: { label: "Connector", order: 6 },
};

/**
 * Connector fields exposed as template variables, keyed by connector type.
 * Only safe (non-secret) fields are listed here.
 *
 * IMPORTANT: Keep in sync with safeConnectorFields in
 * services/b3os-workflow/internal/app/worker/connector_safe_fields.go
 */
export const CONNECTOR_VARIABLE_FIELDS: Record<string, Array<{ name: string; description: string }>> = {
  wallet: [
    { name: "address", description: "Wallet address" },
    { name: "walletName", description: "Wallet name" },
  ],
  session: [
    { name: "walletAddress", description: "Wallet address" },
    { name: "walletName", description: "Wallet name" },
    { name: "accountType", description: "Account type" },
  ],
  polymarket_deposit_wallet: [
    { name: "address", description: "Deposit wallet address" },
    { name: "walletName", description: "Wallet name" },
    { name: "depositWallet", description: "Deposit wallet address (alias)" },
    { name: "accountType", description: "Account type" },
  ],
  railgun: [{ name: "railgunAddress", description: "RAILGUN shielded address" }],
  slack: [
    { name: "teamId", description: "Workspace ID" },
    { name: "teamName", description: "Workspace name" },
    { name: "botUserId", description: "Bot user ID" },
  ],
};

/**
 * Configuration for organizing variables into sub-categories for specific node types.
 * This allows cleaner variable picker UI by grouping related fields together.
 *
 * To add grouping for a new node type:
 * 1. Add an entry to VARIABLE_GROUPING_RULES with the node type as key
 * 2. Define pathMappings: array of { pathPrefix, suffix } pairs
 *    - pathPrefix: the result path prefix to match (e.g., "paymentDetails")
 *    - suffix: the category suffix to append (e.g., "Payment" -> "Node Name - Payment")
 * 3. Optionally set defaultSuffix for paths that don't match any mapping
 */
interface VariableGroupingRule {
  /** Maps result path prefixes to category suffixes */
  pathMappings: Array<{
    /** The path prefix to match (e.g., "paymentDetails", "requestDetails") */
    pathPrefix: string;
    /** The suffix to append to node name (e.g., "Payment" -> "Node Name - Payment") */
    suffix: string;
  }>;
  /** Default suffix for paths not matching any mapping (if not set, uses node name only) */
  defaultSuffix?: string;
}

/**
 * Variable grouping rules by node type.
 * Add entries here to enable sub-category grouping for specific node types.
 */
const VARIABLE_GROUPING_RULES: Record<string, VariableGroupingRule> = {
  // x402 endpoint nodes have response data, payment details, and request metadata
  "call-x402-endpoint": {
    pathMappings: [
      { pathPrefix: "paymentDetails", suffix: "Payment" },
      { pathPrefix: "requestDetails", suffix: "Request" },
    ],
    defaultSuffix: undefined, // Main response fields use just the node name
  },
  // Add more node types here as needed, e.g.:
  // "some-other-action": {
  //   pathMappings: [
  //     { pathPrefix: "metadata", suffix: "Metadata" },
  //     { pathPrefix: "errors", suffix: "Errors" },
  //   ],
  // },
};

// ============================================================================
// SHARED X402 FALLBACK SCHEMA (used when x402 nodes lack resultSchema)
// ============================================================================

/**
 * Fallback field definitions for x402 endpoint nodes without a schema.
 * Shared between flat and hierarchical variable extraction to avoid duplication.
 */
interface X402FallbackField {
  name: string;
  description: string;
  children?: X402FallbackField[];
}

const X402_OUTPUT_FIELDS: X402FallbackField[] = [
  { name: "status", description: "Response status (success or failed)" },
  { name: "statusCode", description: "HTTP status code" },
  { name: "resource", description: "Response data - access nested fields with .fieldName (e.g., resource.symbol)" },
];

const X402_PAYMENT_FIELDS: X402FallbackField[] = [
  {
    name: "paymentDetails",
    description: "Payment transaction details",
    children: [
      { name: "transaction", description: "Transaction hash" },
      { name: "payer", description: "Payer wallet address" },
      { name: "network", description: "Network where payment was made" },
      { name: "asset", description: "Token address used for payment" },
      { name: "amountPaid", description: "Amount paid in smallest token unit" },
    ],
  },
];

const X402_REQUEST_FIELDS: X402FallbackField[] = [
  {
    name: "requestDetails",
    description: "Request details",
    children: [
      { name: "url", description: "The URL that was called" },
      { name: "method", description: "HTTP method used" },
      { name: "hadPayment", description: "Whether payment was made" },
    ],
  },
];

// ============================================================================
// SHARED HELPER FUNCTIONS
// ============================================================================

/**
 * Generate flat WorkflowVariable[] from x402 fallback fields.
 * Used by getNodeOutputVariables for backwards compatibility.
 */
export function generateX402FallbackVariablesFlat(
  nodeId: string,
  nodeName: string,
  getCategoryForPath: (path: string) => string,
): WorkflowVariable[] {
  const variables: WorkflowVariable[] = [];

  // Add output fields
  X402_OUTPUT_FIELDS.forEach(field => {
    variables.push({
      id: `{{${nodeId}.result.${field.name}}}`,
      label: field.description,
      category: getCategoryForPath(field.name),
      nodeId,
      nodeName,
    });
  });

  // Add payment fields with nested children
  X402_PAYMENT_FIELDS.forEach(field => {
    const category = getCategoryForPath(field.name);
    variables.push({
      id: `{{${nodeId}.result.${field.name}}}`,
      label: field.description,
      category,
      nodeId,
      nodeName,
    });
    field.children?.forEach(child => {
      variables.push({
        id: `{{${nodeId}.result.${field.name}.${child.name}}}`,
        label: child.description,
        category,
        nodeId,
        nodeName,
      });
    });
  });

  // Add request fields with nested children
  X402_REQUEST_FIELDS.forEach(field => {
    const category = getCategoryForPath(field.name);
    variables.push({
      id: `{{${nodeId}.result.${field.name}}}`,
      label: field.description,
      category,
      nodeId,
      nodeName,
    });
    field.children?.forEach(child => {
      variables.push({
        id: `{{${nodeId}.result.${field.name}.${child.name}}}`,
        label: child.description,
        category,
        nodeId,
        nodeName,
      });
    });
  });

  return variables;
}

/**
 * Generate hierarchical VariableCategory[] from x402 fallback fields.
 * Used by getNodeHierarchicalVariables for backwards compatibility.
 */
export function generateX402FallbackCategoriesHierarchical(nodeId: string): VariableCategory[] {
  // Helper to convert fallback fields to hierarchical variables
  const toHierarchical = (field: X402FallbackField, parentPath: string): HierarchicalVariable => {
    const variable: HierarchicalVariable = {
      id: `{{${parentPath}.${field.name}}}`,
      key: field.name,
      label: field.description,
    };
    if (field.children) {
      variable.children = field.children.map(child => toHierarchical(child, `${parentPath}.${field.name}`));
    }
    return variable;
  };

  const outputCategory: VariableCategory = {
    key: "output",
    label: "Output",
    order: 1,
    variables: [
      ...X402_OUTPUT_FIELDS.map(f => toHierarchical(f, `${nodeId}.result`)),
      { id: `{{${nodeId}.result}}`, key: "result", label: "Full result object" },
    ],
  };

  const paymentCategory: VariableCategory = {
    key: "payment",
    label: "Payment",
    order: 2,
    variables: X402_PAYMENT_FIELDS.map(f => toHierarchical(f, `${nodeId}.result`)),
  };

  const requestCategory: VariableCategory = {
    key: "request",
    label: "Request",
    order: 3,
    variables: X402_REQUEST_FIELDS.map(f => toHierarchical(f, `${nodeId}.result`)),
  };

  return [outputCategory, paymentCategory, requestCategory];
}

/**
 * Get the category name for a variable based on its path and node type.
 * Uses VARIABLE_GROUPING_RULES if defined for the node type, otherwise returns nodeName.
 */
export function getVariableCategory(nodeType: string, nodeName: string, resultPath: string): string {
  const rule = VARIABLE_GROUPING_RULES[nodeType];
  if (!rule) {
    return nodeName;
  }

  // Check if the path matches any of the configured prefixes
  for (const mapping of rule.pathMappings) {
    if (resultPath === mapping.pathPrefix || resultPath.startsWith(mapping.pathPrefix + ".")) {
      return `${nodeName} - ${mapping.suffix}`;
    }
  }

  // Use default suffix if configured, otherwise just the node name
  return rule.defaultSuffix ? `${nodeName} - ${rule.defaultSuffix}` : nodeName;
}

// ============================================================================
// DISPLAY LABEL UTILITIES
// ============================================================================

/**
 * BFS from root node through edges, returns Map<reactFlowNodeId, stepNumber> (1-based).
 * Filters out placeholder nodes.
 */
export function computeNodeStepNumbers(allNodes: Node[], allEdges: Edge[]): Map<string, number> {
  const stepNumbers = new Map<string, number>();
  const nodesMap = new Map(allNodes.map(n => [n.id, n]));
  const adjList = new Map<string, string[]>();
  for (const edge of allEdges) {
    if (!adjList.has(edge.source)) {
      adjList.set(edge.source, []);
    }
    adjList.get(edge.source)!.push(edge.target);
  }

  // Find root nodes (nodes with no incoming edges)
  const incomingEdgeTargets = new Set(allEdges.map(e => e.target));
  const rootNodes = allNodes.filter(n => !incomingEdgeTargets.has(n.id));
  if (rootNodes.length === 0) return stepNumbers;

  // BFS traversal from all root nodes
  const visited = new Set<string>();
  const queue: string[] = rootNodes.map(n => n.id);
  let stepCounter = 1;

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);

    const node = nodesMap.get(nodeId);
    if (!node || node.type === "placeholder") continue;

    stepNumbers.set(nodeId, stepCounter++);

    // Find children from adjacency list
    const childIds = adjList.get(nodeId) || [];
    for (const childId of childIds) {
      if (!visited.has(childId)) {
        queue.push(childId);
      }
    }
  }

  return stepNumbers;
}

/**
 * Compute a friendly display label for a variable.
 * Format: "{stepNum}. {nodeName}: {fieldPath}"
 * Special vars ($item, @field): formatted with friendly names.
 */
function humanizeFieldPathSegment(segment: string): string {
  const withoutDirectionalPrefix = segment.replace(/^(src|dst)(?=[A-Z])/, "");

  return withoutDirectionalPrefix
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(part => {
      const lower = part.toLowerCase();
      if (lower === "id" || lower === "ids") return part.toUpperCase();
      if (lower === "tx") return "TX";
      if (lower === "url") return "URL";
      if (lower === "api") return "API";
      if (lower === "ip") return "IP";
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    })
    .join(" ");
}

export function humanizeVariableFieldPath(fieldPath: string): string {
  return fieldPath.split(".").filter(Boolean).map(humanizeFieldPathSegment).join(" > ");
}

export function computeDisplayLabel(variableId: string, nodeName: string, stepNumber?: number): string {
  const inner = variableId.slice(2, -2); // Strip {{ }}

  // @ and $ variables: delegate to shared getFriendlyLabel
  const friendly = getFriendlyLabel(inner);
  if (friendly) {
    return friendly;
  }

  // Parse: nodeId.section.field1.field2...
  const parts = inner.split(".");
  if (parts.length < 2) return variableId;

  // Extract field path (everything after nodeId.section)
  const fieldPath = parts.length > 2 ? parts.slice(2).join(".") : parts[1];

  // Trigger-only shortening: trigger names like "Gmail: New Email Received"
  // otherwise produce pills like "Gmail: New Email Received: From". Keep only
  // the part before the first ": " so the pill reads "Gmail: From". Triggers
  // resolve to apiNodeId "root" in B3OS, and variable-name collisions across
  // triggers are unlikely.
  const isTrigger = parts[0] === "root";
  const effectiveNodeName =
    isTrigger && nodeName.includes(": ") ? nodeName.split(": ")[0].trim() || nodeName : nodeName;

  const prefix = stepNumber != null ? `${stepNumber}. ${effectiveNodeName}` : effectiveNodeName;
  return `${prefix}: ${humanizeVariableFieldPath(fieldPath)}`;
}

/**
 * Resolve the run-time value of a variable from node execution states.
 */
export function resolveVariableValue(
  variableId: string,
  nodeStates: NodesExecutionState | undefined,
): string | undefined {
  if (!nodeStates) return undefined;
  const inner = variableId.slice(2, -2); // strip {{ }}
  if (inner.startsWith("$") || inner.startsWith("@")) return undefined;

  const parts = inner.split(".");
  const [nodeId, section, ...rest] = parts;
  const state = nodeStates[nodeId];
  if (!state || state.status !== "success") return undefined;

  let current: unknown = section === "result" ? state.result : section === "input" ? state.input : undefined;
  for (const key of rest) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[key];
  }

  // Key not found in execution result — don't display "null" for missing fields
  if (current === undefined) return undefined;

  return formatRunValue(current);
}

function formatRunValue(value: unknown): string {
  if (value == null) return "null";
  if (typeof value === "string") return value.length > 50 ? `"${value.slice(0, 50)}..."` : `"${value}"`;
  if (typeof value === "object") {
    const json = JSON.stringify(value);
    return json.length > 50 ? json.slice(0, 50) + "..." : json;
  }
  return String(value);
}

/**
 * Check if a variable string (e.g., "{{nodeId.result.field}}") is valid.
 * Special variable types that are always valid:
 * - Template variables ({{@field}}) - reference data object in format action templates
 * - Loop variables ({{$item.*}}, {{$index}}) - reference current item in for-each loops
 *
 * Dynamic paths are also valid if parent field exists:
 * - If {{nodeId.result.resource}} exists, {{nodeId.result.resource.anything}} is valid
 *   (for fields with additionalProperties: true like x402 resource field)
 */
export function isValidVariable(variable: string, availableVariables: WorkflowVariable[]): boolean {
  // Template variables
  if (variable.startsWith("{{@")) return true;

  // Loop variables
  if (variable.startsWith("{{$item") || variable.startsWith("{{$index")) return true;
  if (variable.startsWith("{{$parent_item") || variable.startsWith("{{$parent_index")) return true;
  if (variable.startsWith("{{$for.")) return true;

  // Props variables
  if (variable.startsWith("{{$props.")) return true;

  // System variables
  if (variable === WORKFLOW_ID_VAR) return true;

  // Exact match
  if (availableVariables.some(v => v.id === variable)) return true;

  // Check if this is a nested access on a known dynamic field (additionalProperties: true)
  // If {{nodeId.result.resource}} exists, then {{nodeId.result.resource.city}} is valid
  const availableVarIds = availableVariables.map(v => v.id);
  for (const parentVar of availableVarIds) {
    // Check if this variable is a nested path of an available variable
    // e.g., variable = "{{node.result.resource.city}}", parentVar = "{{node.result.resource}}"
    if (variable.startsWith(parentVar.slice(0, -2) + ".")) {
      return true;
    }
  }

  return false;
}

export function getFallbackVariableDisplayLabel(
  variableId: string,
  allNodes: Node[],
  allEdges: Edge[],
  stepNumbers?: Map<string, number>,
): string | undefined {
  if (!variableId.startsWith("{{") || !variableId.endsWith("}}")) return undefined;

  const inner = variableId.slice(2, -2);
  const friendly = getFriendlyLabel(inner);
  if (friendly) return friendly;

  const parts = inner.split(".");
  if (parts.length < 2) return undefined;

  const [apiNodeId] = parts;
  const node = allNodes.find(candidate => calculateApiNodeId(candidate, allNodes, allEdges) === apiNodeId);
  if (!node) return undefined;

  const nodeName = ((node.data.name as string) || (node.data.type as string) || apiNodeId).trim();
  const stepNumber = stepNumbers?.get(node.id);
  return computeDisplayLabel(variableId, nodeName, stepNumber);
}

// ============================================================================
// GRAPH TRAVERSAL HELPERS (shared by flat, validate, and hierarchical)
// ============================================================================

/**
 * Find all parent nodes (ancestors) of a given node
 */
export function findParentNodes(nodeId: string, edges: Edge[]): string[] {
  const parents: string[] = [];
  const visited = new Set<string>();

  function traverse(currentId: string) {
    if (visited.has(currentId)) return;
    visited.add(currentId);

    // Find edges that point TO this node
    const incomingEdges = edges.filter(e => e.target === currentId);

    incomingEdges.forEach(edge => {
      parents.push(edge.source);
      traverse(edge.source); // Recursively find parents of parents
    });
  }

  traverse(nodeId);
  return parents;
}

/**
 * Find ALL for-each ancestors of a given node, ordered innermost → outermost.
 */
export function findAllForEachAncestors(nodeId: string, allNodes: Node[], edges: Edge[]): Node[] {
  const ancestors: Node[] = [];
  const visited = new Set<string>();

  function traverse(currentId: string): void {
    if (visited.has(currentId)) return;
    visited.add(currentId);

    const incomingEdges = edges.filter(e => e.target === currentId);
    for (const edge of incomingEdges) {
      const parentNode = allNodes.find(n => n.id === edge.source);
      if (!parentNode) continue;

      if (parentNode.data.type === "for-each") {
        ancestors.push(parentNode);
      }
      traverse(edge.source);
    }
  }

  traverse(nodeId);
  return ancestors; // innermost first (nearest ancestor first)
}

/**
 * Find the nearest for-each ancestor of a given node.
 * Returns the for-each node if found, otherwise null.
 */
export function findForEachAncestor(nodeId: string, allNodes: Node[], edges: Edge[]): Node | null {
  const ancestors = findAllForEachAncestors(nodeId, allNodes, edges);
  return ancestors.length > 0 ? ancestors[0] : null;
}

// Helper function to calculate the API node ID (same logic as NodeEditorSidebar)
// Exported for use by VariableInput/VariableTextarea to match variable sources to nodes
export function calculateApiNodeId(node: Node, allNodes: Node[], allEdges: Edge[]): string {
  // Check if the node has a custom apiNodeId stored in data
  const customApiNodeId = node.data.apiNodeId as string | undefined;
  if (customApiNodeId) {
    return customApiNodeId;
  }

  // Find root node (node with no incoming edges)
  const incomingEdges = new Set(allEdges.map(edge => edge.target));
  const rootNode = allNodes.find(n => !incomingEdges.has(n.id));

  // If this is the root node, return "root"
  if (rootNode && node.id === rootNode.id) {
    return "root";
  }

  // Check if this node came from AI or was loaded from an existing workflow
  // These nodes have meaningful IDs that should be preserved (not timestamp-based IDs)
  // Timestamp-based IDs look like: "action-1234567890" or "trigger-1234567890"
  const isTimestampBasedId = /^(action|trigger|logic)-\d+$/.test(node.id);

  // If the node ID is NOT timestamp-based, it came from AI or existing workflow
  // Use the actual node ID directly
  if (!isTimestampBasedId) {
    return node.id;
  }

  // Otherwise, calculate based on type and position (for manually created nodes)
  const nodeType = String(node.data.type || node.type);

  // Count how many nodes of the same type come before this one (in creation order)
  const sameTypeNodes = allNodes.filter(n => {
    if (n.id === rootNode?.id) return false; // Skip root
    return String(n.data.type || n.type) === nodeType;
  });

  // Sort by ID to get consistent ordering (IDs contain timestamps)
  sameTypeNodes.sort((a, b) => a.id.localeCompare(b.id));

  const index = sameTypeNodes.findIndex(n => n.id === node.id);
  const counter = index + 1;

  return `${nodeType}_${counter}`;
}

// ============================================================================
// NON-PARENT CONNECTOR NODE DISCOVERY (shared by flat and hierarchical)
// ============================================================================

/**
 * Info about a non-parent node that has connector variables to expose.
 * Used by both flat and hierarchical extractors.
 */
export interface NonParentConnectorNode {
  apiNodeId: string;
  nodeType: string;
  nodeName: string;
  stepNum: number | undefined;
  fields: Array<{ name: string; description: string }>;
}

/**
 * Find all non-parent nodes that have connector variables to expose.
 * Connector metadata is a static property of a node's configuration, so it
 * should be accessible from any node in the flow — not just downstream ones.
 */
export function findNonParentConnectorNodes(
  currentNodeId: string,
  parentNodeIds: string[],
  allNodes: Node[],
  allEdges: Edge[],
  stepNumbers?: Map<string, number>,
): NonParentConnectorNode[] {
  const parentNodeIdSet = new Set(parentNodeIds);
  const result: NonParentConnectorNode[] = [];

  for (const node of allNodes) {
    if (node.id === currentNodeId || parentNodeIdSet.has(node.id)) continue;
    if (node.type === "placeholder") continue;

    const connectorType = (node.data.connector as WorkflowNodeConnector | undefined)?.type;
    if (!connectorType) continue;

    const fields = CONNECTOR_VARIABLE_FIELDS[connectorType];
    if (!fields || fields.length === 0) continue;

    result.push({
      apiNodeId: calculateApiNodeId(node, allNodes, allEdges),
      nodeType: (node.data.type as string) || "unknown",
      nodeName: (node.data.name as string) || (node.data.type as string) || "unknown",
      stepNum: stepNumbers?.get(node.id),
      fields,
    });
  }

  return result;
}

// ============================================================================
// RE-EXPORTS FROM SUB-FILES
// ============================================================================

export * from "./extract-workflow-variables-flat";
export * from "./extract-workflow-variables-validate";
export * from "./extract-workflow-variables-hierarchical";
